<script lang="ts">
import { selectProps, type SelectOption } from 'naive-ui';

/** One page of results from a `fetch`. */
export interface GaiaRemotePage {
  options: SelectOption[];
  /**
   * Another page exists. Leave it out (or false) and the menu stops paging —
   * scrolling to the bottom asks for nothing more.
   */
  hasMore?: boolean;
}

/**
 * Loads one page of options for a term. `page` starts at 1 and only advances
 * when the previous page came back `hasMore`.
 */
export type GaiaRemoteFetch = (query: string, page: number) => Promise<GaiaRemotePage>;
</script>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch, type PropType } from 'vue';

const props = defineProps({
  ...selectProps,
  /**
   * Loads options for a term. Give this and the field owns its own options,
   * loading flag, debounce and paging — the caller keeps none of it.
   *
   * Leave it out to keep that ownership: pass `options` and `loading` yourself
   * (a store-backed picker does) and this stays the themed incantation plus a
   * debounced `search` event.
   */
  fetch: {
    type: Function as PropType<GaiaRemoteFetch>,
    default: undefined,
  },
  /** Milliseconds after the last keystroke before the term is searched. */
  debounce: {
    type: Number,
    default: 300,
  },
  /**
   * Search the empty term when the menu opens, so it never opens onto an empty
   * list. Every caller of this shape was doing it by hand through
   * `@update:show`.
   */
  primeOnOpen: {
    type: Boolean,
    default: true,
  },
  /**
   * Also search the empty term on mount, so the first open shows results
   * rather than a spinner. Worth it for a field the user is looking at anyway
   * (one already open in a modal), not for one behind a tab.
   */
  primeOnMount: {
    type: Boolean,
    default: false,
  },
  /**
   * Options to keep in the list whenever their value is selected, on top of
   * what `keepSelected` remembers on its own. For a selection the search can
   * never return — an archived playbook the caller looked up by id — which
   * the cache cannot learn about because it has never been in a result.
   */
  seedOptions: {
    type: Array as PropType<SelectOption[]>,
    default: () => [],
  },
  /**
   * Keep the chosen option in the list even once a later search stops
   * returning it. Without this a remote select shows a bare uid where the
   * label should be, which three callers had each worked around separately.
   */
  keepSelected: {
    type: Boolean,
    default: true,
  },
  /** Distance from the bottom of the menu, in px, that asks for the next page. */
  scrollThreshold: {
    type: Number,
    default: 32,
  },
  /**
   * Redeclared, both of them, because naive declares them as a bare `Boolean`
   * — which Vue resolves to `false` (and `true`) whether or not the caller
   * passed anything. A `??` fallback can never fire against that, so the
   * wrapper's intended defaults would silently lose to naive's. Tri-state here
   * makes "not passed" distinguishable, and `undefined` then means "use ours".
   */
  remote: {
    type: Boolean,
    default: undefined,
  },
  clearFilterAfterSelect: {
    type: Boolean,
    default: undefined,
  },
});

const emit = defineEmits<{
  /** The debounced term. Only useful when the caller owns the options. */
  search: [query: string];
  /**
   * A `fetch` rejected. The list keeps whatever it had rather than blanking,
   * so this is for telling the user — every caller already logs inside its own
   * fetch, and nothing here swallows the failure silently.
   */
  error: [error: unknown];
  /**
   * The menu has been scrolled to its last page-worth of rows. Only fires when
   * the caller owns the options — with a `fetch` the field pages itself. Saves
   * a store-backed picker from re-deriving `scrollTop + offsetHeight >=
   * scrollHeight - n`, which both office pickers had written out by hand.
   */
  loadMore: [];
}>();

/* ----------------------------------------------------------------- state */

const owned = computed(() => props.fetch !== undefined);

const fetchedOptions = ref<SelectOption[]>([]);
const fetching = ref(false);
const query = ref('');
const page = ref(1);
const hasMore = ref(false);
/**
 * The labels of options that have been chosen. Kept by value, because that is
 * all the field still holds once a search has replaced the list it came from.
 */
const selectedCache = ref(new Map<string | number, SelectOption>());

/** The current value as a flat list, single and `multiple` alike. */
const selectedValues = computed<(string | number)[]>(() => {
  const value = props.value;
  if (value === null || value === undefined) return [];
  return Array.isArray(value) ? value : [value];
});

const baseOptions = computed<SelectOption[]>(() =>
  owned.value ? fetchedOptions.value : (props.options as SelectOption[])
);

const mergedOptions = computed<SelectOption[]>(() => {
  if (!props.keepSelected) return baseOptions.value;
  const present = new Set(baseOptions.value.map(option => option.value));
  const missing = selectedValues.value
    .filter(value => !present.has(value))
    // A caller's seed wins over the cache: it is the authoritative label.
    .map(
      value =>
        props.seedOptions.find(option => option.value === value) ?? selectedCache.value.get(value)
    )
    .filter((option): option is SelectOption => option !== undefined);
  // Ahead of the results: a chosen row reads as part of the selection, not as
  // the last thing the search happened to return.
  return missing.length > 0 ? [...missing, ...baseOptions.value] : baseOptions.value;
});

// Remember every option the moment it can still be looked up, so it survives
// the search that drops it.
watch(
  [selectedValues, mergedOptions],
  () => {
    for (const value of selectedValues.value) {
      if (selectedCache.value.has(value)) continue;
      const option = mergedOptions.value.find(entry => entry.value === value);
      if (option) selectedCache.value.set(value, option);
    }
  },
  { immediate: true }
);

/* ---------------------------------------------------------------- loading */

/**
 * A plain timer rather than `useDebounceFn`: opening the menu has to search at
 * once and cancel anything already pending, and vueuse's wrapper exposes no
 * cancel — the stale call would land after the primed one and replace its
 * results with an older term's.
 */
let timer: ReturnType<typeof setTimeout> | undefined;

function cancelPending(): void {
  if (timer !== undefined) {
    clearTimeout(timer);
    timer = undefined;
  }
}

onBeforeUnmount(cancelPending);

onMounted(() => {
  if (props.primeOnMount) runSearch('');
});

/**
 * Only the newest request may write. Out-of-order responses would otherwise
 * leave the menu showing the results of a term the user has moved on from.
 */
let requestId = 0;

async function load(term: string, nextPage: number): Promise<void> {
  const fetcher = props.fetch;
  if (!fetcher) return;
  const id = ++requestId;
  fetching.value = true;
  try {
    const result = await fetcher(term, nextPage);
    if (id !== requestId) return;
    fetchedOptions.value =
      nextPage === 1 ? result.options : [...fetchedOptions.value, ...result.options];
    hasMore.value = result.hasMore === true;
    page.value = nextPage;
  } catch (error) {
    // Caught rather than left to reject: `load` is called from an event
    // handler, so a throw would surface as an unhandled rejection and strand
    // the spinner. The list keeps what it had and the caller is told.
    if (id === requestId) emit('error', error);
  } finally {
    if (id === requestId) fetching.value = false;
  }
}

function runSearch(term: string): void {
  cancelPending();
  query.value = term;
  emit('search', term);
  if (owned.value) void load(term, 1);
}

function handleSearch(term: string): void {
  cancelPending();
  if (props.debounce <= 0) {
    runSearch(term);
    return;
  }
  timer = setTimeout(() => {
    timer = undefined;
    runSearch(term);
  }, props.debounce);
}

// A new `fetch` is a new source (a picker switched from people to roles):
// search the current term against it, so the menu never shows the old
// source's rows. The chosen options' labels are cached, so chips survive.
watch(
  () => props.fetch,
  () => {
    if (owned.value) runSearch(query.value);
  }
);

/**
 * Empty the typed term and search afresh — for a caller that switches what the
 * field searches. naive keeps the term internally with no prop or method to set
 * it, so this writes its `pattern` (naive-ui 2.45); recheck on a naive upgrade.
 */
const selectRef = ref<{ pattern: string } | null>(null);

function clearSearch(): void {
  if (selectRef.value) selectRef.value.pattern = '';
  runSearch('');
}

defineExpose({ clearSearch });

function handleShow(show: boolean): void {
  if (!show || !props.primeOnOpen) return;
  // Immediately, cancelling any pending keystroke: the menu is on screen now.
  runSearch('');
}

function handleScroll(event: Event): void {
  const target = event.currentTarget;
  if (!(target instanceof HTMLElement)) return;
  if (target.scrollTop + target.offsetHeight < target.scrollHeight - props.scrollThreshold) return;
  if (owned.value) {
    if (hasMore.value && !fetching.value) void load(query.value, page.value + 1);
    return;
  }
  emit('loadMore');
}

/* --------------------------------------------------------------- bindings */

/**
 * The wrapper's own props come out before the spread — `v-bind`ing them onto
 * n-select would land them on the DOM element as stray attributes. `options`
 * and `loading` are re-derived in fetch mode, and `clearFilterAfterSelect`
 * defaults off: keeping the term is what lets a multi-select pick two results
 * out of one search.
 */
const selectBindings = computed<Record<string, unknown>>(() => {
  const bindings: Record<string, unknown> = { ...props };
  delete bindings.fetch;
  delete bindings.debounce;
  delete bindings.primeOnOpen;
  delete bindings.primeOnMount;
  delete bindings.seedOptions;
  delete bindings.keepSelected;
  delete bindings.scrollThreshold;
  bindings.filterable = true;
  bindings.remote = props.remote ?? true;
  bindings.options = mergedOptions.value;
  if (owned.value) bindings.loading = props.loading || fetching.value;
  bindings.clearFilterAfterSelect = props.clearFilterAfterSelect ?? false;
  return bindings;
});
</script>

<template>
  <!-- The remote-search select: `filterable remote clearable` plus a loading
       flag, a debounced @search, a prime-on-open @update:show and, where the
       endpoint pages, an @scroll — the five-prop incantation thirteen call
       sites each spelled out, with their own debounce beside it. -->
  <n-select
    ref="selectRef"
    v-bind="selectBindings"
    @search="handleSearch"
    @update:show="handleShow"
    @scroll="handleScroll"
  >
    <!-- Above the options — a scope switch, say. -->
    <template v-if="$slots.header" #header>
      <slot name="header" />
    </template>
  </n-select>
</template>
